import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { FloppyDisk, ArrowSquareOut, Trash, Plus, Eye, Share, Globe, ChartLineUp, WhatsappLogo, CursorClick, TrendUp, DownloadSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { JoinCooperativeView } from './JoinCooperativePage';
import { createLandingDraftShare } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

const API = process.env.REACT_APP_BACKEND_URL;

function LandingAnalyticsCard() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/landing/analytics?days=${days}`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [days]);

  const t = stats?.totals || { page_view: 0, share_whatsapp: 0, cta_clicks_total: 0 };
  const maxDaily = Math.max(1, ...(stats?.daily || []).map((d) => d.page_view || 0));

  const exportCsv = () => {
    if (!stats) return;
    const rows = [
      ['Période', `${stats.since} → aujourd'hui (${stats.days} jours)`],
      ['Taux de conversion (%)', String(stats.conversion_rate ?? 0)],
      [],
      ['Total visites', String(t.page_view)],
      ['Total partages WhatsApp', String(t.share_whatsapp)],
      ['Total clics CTA (hero)', String(t.cta_click_hero)],
      ['Total clics CTA (bas de page)', String(t.cta_click_bottom)],
      ['Total clics CTA (login hero)', String(t.cta_click_login_hero)],
      ['Total clics CTA', String(t.cta_clicks_total)],
      [],
      ['Jour', 'Visites', 'Partages WhatsApp', 'Clics CTA'],
      ...(stats.daily || []).map((d) => [d.day, String(d.page_view), String(d.share_whatsapp), String(d.cta_clicks)]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `landing_analytics_${stats.since}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  const exportPdf = () => {
    if (!stats) return;
    const html = `<!DOCTYPE html>
<html lang="fr"><head><meta charset="utf-8"><title>Analytics landing publique</title>
<style>
  body { font-family: 'Manrope', -apple-system, sans-serif; color: #1A2421; padding: 32px; }
  h1 { color: #2D5A4C; border-bottom: 3px solid #80C51F; padding-bottom: 8px; }
  .meta { color: #5E6D66; font-size: 12px; margin-bottom: 24px; }
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 32px; }
  .kpi { border: 1px solid #E8E6E1; border-radius: 12px; padding: 16px; }
  .kpi .label { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #5E6D66; font-weight: 700; }
  .kpi .value { font-size: 28px; font-weight: 800; color: #1A2421; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #E8E6E1; }
  th { background: #F5F4EF; font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 1px; color: #5E6D66; }
  tr:nth-child(even) td { background: #F9F8F5; }
  .footer { margin-top: 32px; font-size: 10px; color: #8A968F; border-top: 1px solid #E8E6E1; padding-top: 12px; }
</style></head><body>
<h1>Analytics landing publique</h1>
<p class="meta">Période : ${stats.since} → aujourd'hui (${stats.days} jours) — Taux de conversion : <strong>${stats.conversion_rate ?? 0} %</strong></p>
<div class="kpi-grid">
  <div class="kpi"><div class="label">Visites</div><div class="value">${t.page_view.toLocaleString('fr-FR')}</div></div>
  <div class="kpi"><div class="label">Partages WhatsApp</div><div class="value">${t.share_whatsapp.toLocaleString('fr-FR')}</div></div>
  <div class="kpi"><div class="label">Clics CTA</div><div class="value">${t.cta_clicks_total.toLocaleString('fr-FR')}</div></div>
  <div class="kpi"><div class="label">Taux conversion</div><div class="value">${stats.conversion_rate ?? 0} %</div></div>
</div>
<table>
  <thead><tr><th>Jour</th><th>Visites</th><th>Partages WhatsApp</th><th>Clics CTA</th></tr></thead>
  <tbody>${(stats.daily || []).map((d) => `<tr><td>${d.day}</td><td>${d.page_view}</td><td>${d.share_whatsapp}</td><td>${d.cta_clicks}</td></tr>`).join('')}</tbody>
</table>
<p class="footer">Objectif SCOP Outremer — export généré le ${new Date().toLocaleString('fr-FR')}</p>
<script>window.onload = () => setTimeout(() => window.print(), 250);</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup bloquée — autorisez-la pour exporter en PDF'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    toast.success('Aperçu PDF ouvert — utilisez « Enregistrer au format PDF »');
  };

  const Kpi = ({ label, value, icon: Icon, color, testId }) => (
    <div className="flex items-center gap-3 rounded-xl border border-[#E8E6E1] bg-white px-4 py-3" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: color }}>
        <Icon size={20} weight="fill" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-semibold">{label}</p>
        <p className="text-2xl font-extrabold text-[#1A2421] leading-tight" style={{ fontFamily: 'Manrope' }}>{value}</p>
      </div>
    </div>
  );

  return (
    <Card data-testid="landing-analytics-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ChartLineUp size={18} weight="duotone" className="text-[#2D5A4C]" />
          Analytics landing publique
        </CardTitle>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={!stats || loading}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-[#E8E6E1] text-[#2D5A4C] hover:bg-[#F5F4EF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="analytics-export-csv"
            title="Télécharger les statistiques au format CSV"
          >
            <DownloadSimple size={14} weight="bold" /> CSV
          </button>
          <button
            onClick={exportPdf}
            disabled={!stats || loading}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-[#E8E6E1] text-[#EA3C3A] hover:bg-[#EA3C3A]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="analytics-export-pdf"
            title="Ouvrir la version imprimable / PDF"
          >
            <DownloadSimple size={14} weight="bold" /> PDF
          </button>
          <div className="flex items-center gap-1 bg-[#F5F4EF] rounded-md p-0.5 border border-[#E8E6E1]">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 text-xs font-semibold rounded transition-colors ${days === d ? 'bg-[#2D5A4C] text-white' : 'text-[#5E6D66] hover:text-[#1A2421]'}`}
                data-testid={`analytics-range-${d}`}
              >
                {d}j
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-[#5E6D66] flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Chargement…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Visites" value={t.page_view.toLocaleString('fr-FR')} icon={Eye} color="#00AEEF" testId="kpi-page-view" />
              <Kpi label="Partages WhatsApp" value={t.share_whatsapp.toLocaleString('fr-FR')} icon={WhatsappLogo} color="#25D366" testId="kpi-share-whatsapp" />
              <Kpi label="Clics CTA" value={t.cta_clicks_total.toLocaleString('fr-FR')} icon={CursorClick} color="#FF8E00" testId="kpi-cta-clicks" />
              <Kpi label="Taux conversion" value={`${stats?.conversion_rate ?? 0} %`} icon={TrendUp} color="#EA3C3A" testId="kpi-conversion-rate" />
            </div>
            {stats?.daily?.length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-semibold mb-2">Visites quotidiennes</p>
                <div className="flex items-end gap-1 h-24 bg-[#F9F8F5] rounded-lg p-2" data-testid="analytics-daily-chart">
                  {stats.daily.map((d) => {
                    const h = Math.max(2, Math.round((d.page_view / maxDaily) * 88));
                    return (
                      <div key={d.day} className="flex-1 flex flex-col items-center justify-end min-w-0" title={`${d.day} — ${d.page_view} visites, ${d.share_whatsapp} partages`}>
                        <div className="w-full rounded-t bg-gradient-to-t from-[#2D5A4C] to-[#00AEEF]" style={{ height: `${h}px` }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Champ ligne libellé + textarea sortable en dehors du composant
const F = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">{label}</Label>
    {children}
  </div>
);

// ArrayEditor : liste de chaînes éditables
function ArrayEditor({ label, values, onChange, testId }) {
  const arr = Array.isArray(values) ? values : [];
  return (
    <div className="space-y-2">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Éditeur landing publique"
        subtitle="Configuration du contenu affiché sur /rejoindre"
        icon={<Globe size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#00AEEF', '#D5007D']}
      />

      <Label className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">{label}</Label>
      <div className="space-y-1.5" data-testid={testId}>
        {arr.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={v} onChange={(e) => { const c = [...arr]; c[i] = e.target.value; onChange(c); }} />
            <Button variant="ghost" size="sm" onClick={() => onChange(arr.filter((_, k) => k !== i))} className="text-red-600"><Trash size={14} /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => onChange([...arr, ''])}><Plus size={14} className="mr-1" /> Ajouter</Button>
      </div>
    </div>
  );
}

export default function LandingEditorPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [showPreview, setShowPreview] = useState(true);
  const [previewMode, setPreviewMode] = useState('draft'); // 'draft' = état local en direct, 'published' = iframe /rejoindre

  const isAdmin = user && ['super_admin', 'admin', 'direction'].includes(user.role);

  useEffect(() => {
    axios.get(`${API}/api/public/landing`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Chargement impossible'))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <div className="p-8 text-[#5E6D66]">Accès réservé aux administrateurs.</div>;
  if (loading || !data) return <div className="p-8 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>;

  const patch = (key, value) => setData((d) => ({ ...d, [key]: value }));
  const patchHero = (key, value) => setData((d) => ({ ...d, hero: { ...(d.hero || {}), [key]: value } }));
  const patchCta = (key, value) => setData((d) => ({ ...d, cta: { ...(d.cta || {}), [key]: value } }));
  const patchContact = (key, value) => setData((d) => ({ ...d, contact: { ...(d.contact || {}), [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      // Nettoyage des chaînes vides dans les tableaux
      const clean = JSON.parse(JSON.stringify(data));
      ['subtitle_steps', 'pillars'].forEach((k) => { if (Array.isArray(clean[k])) clean[k] = clean[k].filter(Boolean); });
      if (clean.hero?.territoires) clean.hero.territoires = clean.hero.territoires.filter(Boolean);
      (clean.profiles || []).forEach((p) => { if (Array.isArray(p.bullets)) p.bullets = p.bullets.filter(Boolean); });
      (clean.conventions || []).forEach((c) => { if (Array.isArray(c.bullets)) c.bullets = c.bullets.filter(Boolean); });
      await axios.patch(`${API}/api/public/landing`, clean, { withCredentials: true });
      setPreviewKey((k) => k + 1); // recharge l'iframe après publication
      toast.success('Landing publiée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    }
    setSaving(false);
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px] mx-auto" data-testid="landing-editor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Éditeur landing publique</h1>
          <p className="text-sm text-[#5E6D66]">Contenu servi sur <code className="bg-[#F5F4EF] px-1.5 py-0.5 rounded text-xs">/rejoindre</code> — publication instantanée.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} data-testid="landing-toggle-preview">
            {showPreview ? 'Masquer' : 'Afficher'} l'aperçu
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await createLandingDraftShare(data);
                const full = `${window.location.origin}${r.data.url_path}`;
                await navigator.clipboard.writeText(full).catch(() => {});
                toast.success('Lien brouillon copié — expire dans 14 jours');
                window.open(full, '_blank');
              } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de partage'); }
            }}
            data-testid="landing-share-draft"
            title="Générer un lien secret qui affiche le brouillon actuel"
          >
            <Share size={14} className="mr-1" /> Partager brouillon
          </Button>
          <Button variant="outline" onClick={() => window.open('/rejoindre', '_blank')} data-testid="landing-preview">
            <ArrowSquareOut size={14} className="mr-1" /> Ouvrir
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="landing-save">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <FloppyDisk size={14} className="mr-1" />} Publier
          </Button>
        </div>
      </div>

      <LandingAnalyticsCard />

      <div className={`grid gap-6 ${showPreview ? 'xl:grid-cols-[minmax(0,1fr)_minmax(0,540px)]' : 'grid-cols-1'}`}>
        <div className="min-w-0">
      <Tabs defaultValue="hero" className="w-full">
        <TabsList className="flex flex-wrap gap-1 justify-start bg-transparent border border-[#E8E6E1] rounded-lg p-1 h-auto">
          {['hero', 'highlight', 'profiles', 'advantages', 'conventions', 'steps', 'cta', 'contact'].map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="data-[state=active]:bg-[#2D5A4C] data-[state=active]:text-white capitalize">{k}</TabsTrigger>
          ))}
        </TabsList>

        {/* HERO */}
        <TabsContent value="hero">
          <Card><CardHeader><CardTitle className="text-base">Hero</CardTitle></CardHeader><CardContent className="space-y-4">
            <F label="Titre principal"><Input value={data.title || ''} onChange={(e) => patch('title', e.target.value)} data-testid="edit-title" /></F>
            <F label="Tagline hero"><Input value={data.hero?.tagline || ''} onChange={(e) => patchHero('tagline', e.target.value)} /></F>
            <F label="Sous-tagline (bas hero)"><Input value={data.hero?.footer_tagline || ''} onChange={(e) => patchHero('footer_tagline', e.target.value)} /></F>
            <ArrayEditor label="Sous-titres animés (S'engager, Coopérer…)" values={data.subtitle_steps} onChange={(v) => patch('subtitle_steps', v)} testId="edit-subtitle-steps" />
            <ArrayEditor label="Territoires desservis" values={data.hero?.territoires} onChange={(v) => patchHero('territoires', v)} testId="edit-territoires" />
          </CardContent></Card>
        </TabsContent>

        {/* HIGHLIGHT */}
        <TabsContent value="highlight">
          <Card><CardHeader><CardTitle className="text-base">Bandeau vert (highlight)</CardTitle></CardHeader><CardContent>
            <F label="Message"><Textarea rows={3} value={data.highlight || ''} onChange={(e) => patch('highlight', e.target.value)} data-testid="edit-highlight" /></F>
          </CardContent></Card>
        </TabsContent>

        {/* PROFILES */}
        <TabsContent value="profiles">
          <div className="space-y-4">
            {(data.profiles || []).map((p, i) => (
              <Card key={i}><CardHeader><CardTitle className="text-base flex items-center gap-2">
                Profil <Badge className={p.kind === 'pp' ? 'bg-[#EA3C3A]' : 'bg-[#00AEEF]'}>{p.kind === 'pp' ? 'Personne physique' : 'Personne morale'}</Badge>
              </CardTitle></CardHeader><CardContent className="space-y-3">
                <F label="Titre"><Input value={p.title || ''} onChange={(e) => { const c = [...data.profiles]; c[i] = { ...c[i], title: e.target.value }; patch('profiles', c); }} /></F>
                <F label="Sous-titre"><Input value={p.subtitle || ''} onChange={(e) => { const c = [...data.profiles]; c[i] = { ...c[i], subtitle: e.target.value }; patch('profiles', c); }} /></F>
                <ArrayEditor label="Points clés" values={p.bullets} onChange={(v) => { const c = [...data.profiles]; c[i] = { ...c[i], bullets: v }; patch('profiles', c); }} />
                {p.college && (
                  <div className="space-y-2 pt-2 border-t border-[#E8E6E1]">
                    <F label="Collège auto — libellé"><Input value={p.college.label || ''} onChange={(e) => { const c = [...data.profiles]; c[i] = { ...c[i], college: { ...c[i].college, label: e.target.value } }; patch('profiles', c); }} /></F>
                    <F label="Note collège"><Input value={p.college.note || ''} onChange={(e) => { const c = [...data.profiles]; c[i] = { ...c[i], college: { ...c[i].college, note: e.target.value } }; patch('profiles', c); }} /></F>
                  </div>
                )}
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        {/* ADVANTAGES */}
        <TabsContent value="advantages">
          <Card><CardHeader><CardTitle className="text-base">7 avantages</CardTitle></CardHeader><CardContent className="space-y-3">
            {(data.advantages || []).map((a, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-2 p-3 rounded-lg border border-[#E8E6E1]">
                <F label={`Titre #${i + 1}`}><Input value={a.title || ''} onChange={(e) => { const c = [...data.advantages]; c[i] = { ...c[i], title: e.target.value }; patch('advantages', c); }} /></F>
                <F label="Icône (network, growth, support, vote, impact, finance, star)"><Input value={a.icon || ''} onChange={(e) => { const c = [...data.advantages]; c[i] = { ...c[i], icon: e.target.value }; patch('advantages', c); }} /></F>
                <div className="md:col-span-2">
                  <F label="Texte"><Textarea rows={2} value={a.text || ''} onChange={(e) => { const c = [...data.advantages]; c[i] = { ...c[i], text: e.target.value }; patch('advantages', c); }} /></F>
                </div>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        {/* CONVENTIONS */}
        <TabsContent value="conventions">
          <div className="space-y-4">
            {(data.conventions || []).map((c, i) => (
              <Card key={i}><CardHeader><CardTitle className="text-base">{c.code}</CardTitle></CardHeader><CardContent className="space-y-3">
                <F label="Titre court"><Input value={c.title || ''} onChange={(e) => { const cs = [...data.conventions]; cs[i] = { ...cs[i], title: e.target.value }; patch('conventions', cs); }} /></F>
                <F label="Titre long"><Input value={c.long_title || ''} onChange={(e) => { const cs = [...data.conventions]; cs[i] = { ...cs[i], long_title: e.target.value }; patch('conventions', cs); }} /></F>
                <F label="Intro"><Textarea rows={2} value={c.intro || ''} onChange={(e) => { const cs = [...data.conventions]; cs[i] = { ...cs[i], intro: e.target.value }; patch('conventions', cs); }} /></F>
                <ArrayEditor label="Points clés" values={c.bullets} onChange={(v) => { const cs = [...data.conventions]; cs[i] = { ...cs[i], bullets: v }; patch('conventions', cs); }} />
                <F label="Devise (motto)"><Input value={c.motto || ''} onChange={(e) => { const cs = [...data.conventions]; cs[i] = { ...cs[i], motto: e.target.value }; patch('conventions', cs); }} /></F>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        {/* STEPS */}
        <TabsContent value="steps">
          <Card><CardHeader><CardTitle className="text-base">5 étapes d'adhésion</CardTitle></CardHeader><CardContent className="space-y-2">
            {(data.steps || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#EA3C3A] text-white font-bold flex items-center justify-center">{s.n}</span>
                <Input value={s.label || ''} onChange={(e) => { const c = [...data.steps]; c[i] = { ...c[i], label: e.target.value }; patch('steps', c); }} />
              </div>
            ))}
            <ArrayEditor label="Piliers (4 tags bas de page)" values={data.pillars} onChange={(v) => patch('pillars', v)} />
          </CardContent></Card>
        </TabsContent>

        {/* CTA */}
        <TabsContent value="cta">
          <Card><CardHeader><CardTitle className="text-base">Call to Action</CardTitle></CardHeader><CardContent className="space-y-3">
            <F label="Libellé du bouton"><Input value={data.cta?.label || ''} onChange={(e) => patchCta('label', e.target.value)} data-testid="edit-cta-label" /></F>
            <F label="Lien (href)"><Input value={data.cta?.href || ''} onChange={(e) => patchCta('href', e.target.value)} /></F>
            <F label="Phrase sous le CTA"><Input value={data.cta?.tail || ''} onChange={(e) => patchCta('tail', e.target.value)} /></F>
          </CardContent></Card>
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact">
          <Card><CardHeader><CardTitle className="text-base">Pied de page / contact</CardTitle></CardHeader><CardContent className="space-y-3">
            <F label="Tagline footer"><Input value={data.contact?.tagline || ''} onChange={(e) => patchContact('tagline', e.target.value)} /></F>
            <F label="Email"><Input value={data.contact?.email || ''} onChange={(e) => patchContact('email', e.target.value)} data-testid="edit-contact-email" /></F>
            <F label="Site web (sans https://)"><Input value={data.contact?.website || ''} onChange={(e) => patchContact('website', e.target.value)} /></F>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
        </div>

        {showPreview && (
          <div className="min-w-0 xl:sticky xl:top-4 xl:self-start" data-testid="landing-preview-pane">
            <div className="rounded-2xl border border-[#E8E6E1] bg-white shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#E8E6E1] bg-[#F5F4EF]">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#EA3C3A]/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF8E00]/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#80C51F]/80" />
                </div>
                <div className="flex items-center gap-1 bg-white rounded-md p-0.5 border border-[#E8E6E1]">
                  <button
                    onClick={() => setPreviewMode('draft')}
                    className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider transition-colors ${previewMode === 'draft' ? 'bg-[#2D5A4C] text-white' : 'text-[#5E6D66]'}`}
                    data-testid="preview-mode-draft"
                  >
                    <Eye size={10} className="inline mr-0.5" /> Brouillon
                  </button>
                  <button
                    onClick={() => setPreviewMode('published')}
                    className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider transition-colors ${previewMode === 'published' ? 'bg-[#2D5A4C] text-white' : 'text-[#5E6D66]'}`}
                    data-testid="preview-mode-published"
                  >
                    Publié
                  </button>
                </div>
                <button
                  onClick={() => setPreviewKey((k) => k + 1)}
                  className="text-[10px] text-[#2D5A4C] hover:underline font-medium"
                  data-testid="landing-preview-refresh"
                >
                  Rafraîchir
                </button>
              </div>
              {previewMode === 'draft' ? (
                <div
                  className="overflow-auto bg-white"
                  style={{ height: '78vh' }}
                  data-testid="landing-preview-draft"
                >
                  <div
                    style={{ width: '1400px', transform: 'scale(0.36)', transformOrigin: 'top left', pointerEvents: 'none' }}
                    aria-hidden
                  >
                    <JoinCooperativeView data={data} />
                  </div>
                </div>
              ) : (
                <iframe
                  key={previewKey}
                  src="/rejoindre"
                  title="Aperçu landing"
                  className="w-full block bg-white"
                  style={{ height: '78vh', border: 0 }}
                  data-testid="landing-preview-iframe"
                />
              )}
            </div>
            <p className="text-[11px] text-[#5E6D66] mt-2 text-center italic">
              {previewMode === 'draft'
                ? 'Aperçu brouillon en direct — reflète vos modifications avant publication.'
                : "L'aperçu se met à jour après « Publier » — les changements sont visibles immédiatement pour tous les visiteurs."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
